import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export interface UseSearchQueryParamsReturn {
  entityId: string | null;
  templateId: string | null;
  status: string | null;
  offset: number;
  setEntityId: (entityId: string | null) => void;
  setTemplateId: (templateId: string | null) => void;
  setStatus: (status: string | null) => void;
  setOffset: (offset: number) => void;
  updateQueryParams: (updates: {
    entityId?: string | null;
    templateId?: string | null;
    status?: string | null;
    offset?: number;
  }) => void;
  removeEntityId: () => void;
}

/**
 * Unified hook for search page query parameters.
 * @param baseRoute - The base route for the search page (e.g., FREIGHTS.search or TRANSPORT.search)
 * @param entityParamName - The URL parameter name for the entity ID (e.g., 'cargoId' or 'transportId')
 */
export const useSearchQueryParams = (
  baseRoute: string,
  entityParamName: string = 'entityId',
): UseSearchQueryParamsReturn => {
  const location = useLocation();
  const navigate = useNavigate();

  const params = new URLSearchParams(location.search);
  const entityId = params.get(entityParamName);
  const templateId = params.get('templateId');
  const status = params.get('status');
  const offset = parseInt(params.get('offset') || '0', 10);

  const updateQueryParams = useCallback(
    (updates: {
      entityId?: string | null;
      templateId?: string | null;
      status?: string | null;
      offset?: number;
    }) => {
      const currentParams = new URLSearchParams(location.search);

      if (updates.entityId !== undefined) {
        if (updates.entityId) {
          currentParams.set(entityParamName, updates.entityId);
        } else {
          currentParams.delete(entityParamName);
        }
      }

      if (updates.templateId !== undefined) {
        if (updates.templateId) {
          currentParams.set('templateId', updates.templateId);
        } else {
          currentParams.delete('templateId');
        }
      }

      if (updates.status !== undefined) {
        if (updates.status) {
          currentParams.set('status', updates.status);
        } else {
          currentParams.delete('status');
        }
      }

      if (updates.offset !== undefined) {
        if (updates.offset && updates.offset > 0) {
          currentParams.set('offset', String(updates.offset));
        } else {
          currentParams.delete('offset');
        }
      }

      const newUrl = currentParams.toString()
        ? `${baseRoute}?${currentParams.toString()}`
        : baseRoute;
      navigate(newUrl, { replace: true });
    },
    [baseRoute, entityParamName, location.search, navigate],
  );

  const setEntityId = useCallback(
    (newEntityId: string | null) => {
      updateQueryParams({ entityId: newEntityId });
    },
    [updateQueryParams],
  );

  const setTemplateId = useCallback(
    (newTemplateId: string | null) => {
      updateQueryParams({ templateId: newTemplateId });
    },
    [updateQueryParams],
  );

  const setStatus = useCallback(
    (newStatus: string | null) => {
      updateQueryParams({ status: newStatus });
    },
    [updateQueryParams],
  );

  const setOffset = useCallback(
    (newOffset: number) => {
      updateQueryParams({ offset: newOffset });
    },
    [updateQueryParams],
  );

  const removeEntityId = useCallback(() => {
    updateQueryParams({ entityId: null });
  }, [updateQueryParams]);

  return {
    entityId,
    templateId,
    status,
    offset,
    setEntityId,
    setTemplateId,
    setStatus,
    setOffset,
    updateQueryParams,
    removeEntityId,
  };
};
